/**
 * Copiloto — Genio & Luca mock engine
 *
 * All functions return hardcoded mock responses for now.
 * TODO: Replace mocks with real Claude API calls when
 * ANTHROPIC_API_KEY is configured.
 */

const MOCK_DELAY = 800; // Simulate API latency

const delay = (ms) => new Promise(r => setTimeout(r, ms));

// Supuestos keywords that trigger highlights
const SUPUESTOS_PATTERNS = [
  'todos los emprendedores', 'siempre', 'nunca', 'es obvio que',
  'nadie', 'todo el mundo', 'claramente', 'sin duda',
  'es imposible', 'cualquiera sabe', 'todo mundo sabe',
];

/**
 * Detect supuestos (assumptions) in text
 */
export function detectarSupuestos(texto) {
  const lower = texto.toLowerCase();
  return SUPUESTOS_PATTERNS.filter(p => lower.includes(p)).map(p => ({
    patron: p,
    tipo: 'supuesto',
    mensaje: `Luca: "${p}" es un supuesto. ¿Tienes datos que lo respalden?`,
  }));
}

/**
 * Analyze a pensamiento text and return observations
 * TODO: Replace with Claude API call
 */
export async function analizarPensamiento(texto) {
  await delay(MOCK_DELAY);

  const supuestos = detectarSupuestos(texto);
  const wordCount = texto.split(/\s+/).filter(Boolean).length;

  const observaciones = [];

  if (supuestos.length > 0) {
    observaciones.push({
      personaje: 'luca',
      tipo: 'supuesto',
      mensaje: supuestos[0].mensaje,
    });
  }

  if (wordCount > 20) {
    observaciones.push({
      personaje: 'genio',
      tipo: 'conexion',
      mensaje: 'Genio: Esto me recuerda a lo que escribio Eduardo sobre validar antes de construir. ¿Quieres conectarlo?',
    });
  }

  if (wordCount > 50) {
    observaciones.push({
      personaje: 'luca',
      tipo: 'claridad',
      mensaje: 'Luca: Tu idea tiene potencial pero necesita mas foco. ¿Cual es la accion concreta que propones?',
    });
  }

  return {
    observaciones,
    stats: {
      palabras: wordCount,
      supuestos: supuestos.length,
      claridad: supuestos.length === 0 ? 'alta' : 'media',
    },
  };
}

/**
 * Execute a copiloto command
 * TODO: Replace with Claude API call
 */
export async function ejecutarComando(comando, query, contexto = '') {
  await delay(MOCK_DELAY);

  switch (comando) {
    case '?web':
      return {
        personaje: 'genio',
        tipo: 'busqueda_web',
        titulo: `Genio busco en web: "${query}"`,
        mock: true,
        resultados: [
          { titulo: `${query} — Guia practica para emprendedores`, url: 'https://ejemplo.com/guia', fuente: 'ejemplo.com' },
          { titulo: `Como aplicar ${query} en tu negocio`, url: 'https://ejemplo.com/aplicar', fuente: 'ejemplo.com' },
          { titulo: `${query}: lo que nadie te dice`, url: 'https://ejemplo.com/verdad', fuente: 'ejemplo.com' },
        ],
      };

    case '?red':
      return {
        personaje: 'genio',
        tipo: 'busqueda_red',
        titulo: `Genio encontro en T&D: "${query}"`,
        mock: true,
        resultados: [
          { nombre: 'Eduardo', pensamiento: `Reflexion sobre ${query}`, match: '87%' },
          { nombre: 'Karla', pensamiento: `Mi experiencia con ${query}`, match: '72%' },
        ],
      };

    case '!opinion':
      return {
        personaje: 'luca',
        tipo: 'opinion',
        mensaje: 'Luca: Va bien la direccion, pero te falta aterrizar. ¿Cual es el primer paso concreto que vas a dar esta semana? No me digas "investigar mas". Dame algo medible.',
      };

    case '!plan':
      return {
        personaje: 'luca',
        tipo: 'plan',
        titulo: 'Plan de 7 dias de Luca',
        pasos: [
          { dia: 1, tarea: 'Define tu cliente ideal en 1 parrafo' },
          { dia: 2, tarea: 'Habla con 3 personas de ese perfil' },
          { dia: 3, tarea: 'Identifica el patron de dolor comun' },
          { dia: 4, tarea: 'Escribe tu propuesta de valor en 1 oracion' },
          { dia: 5, tarea: 'Diseña tu oferta minima viable' },
          { dia: 6, tarea: 'Ponle precio y justificalo' },
          { dia: 7, tarea: 'Publica tu pensamiento en La Sala' },
        ],
      };

    case '+ideas':
      return {
        personaje: 'genio',
        tipo: 'ideas',
        titulo: '5 chispas de Genio',
        ideas: [
          '¿Y si le vendes primero a quien ya te conoce?',
          '¿Que pasaria si duplicas el precio y das el doble de valor?',
          '¿Puedes resolver esto en 48 horas sin gastar nada?',
          '¿Quien en tu red ya resolvio este problema?',
          '¿Que harias si solo tuvieras 1 semana para lanzar?',
        ],
      };

    case '+conexiones':
      return {
        personaje: 'genio',
        tipo: 'conexiones',
        titulo: 'Wikilinks sugeridos por Genio',
        sugerencias: [
          { titulo: 'Cobrar por valor vs por hora', match: '91%' },
          { titulo: 'El primer cliente nunca es el ideal', match: '78%' },
          { titulo: 'Validar antes de construir', match: '65%' },
        ],
      };

    case '/resumen':
      return {
        personaje: 'luca',
        tipo: 'resumen',
        mensaje: contexto
          ? `Luca resume: Estas hablando de tu proyecto y como diferenciarte. El punto central es que necesitas definir que te hace unico. ${contexto.split(/\s+/).length} palabras analizadas.`
          : 'Luca: No hay suficiente texto para resumir. Escribe al menos 3 oraciones.',
      };

    case '/publicar':
      return {
        personaje: 'luca',
        tipo: 'accion',
        mensaje: 'Luca: Listo para publicar. Usa el boton "Publicar pensamiento" cuando estes seguro.',
      };

    case '/privado':
      return {
        personaje: 'luca',
        tipo: 'accion',
        mensaje: 'Luca: Guardado como idea privada. Solo tu puedes verla en Mis Ideas.',
      };

    default:
      return {
        personaje: 'genio',
        tipo: 'error',
        mensaje: `Genio: No conozco el comando "${comando}". Revisa la tabla de comandos.`,
      };
  }
}

/**
 * Parse command from text: [?web query], [!opinion], [/resumen], etc.
 * Returns { comando, query } or null
 */
export function parseCommand(text) {
  const match = text.match(/\[([?!+/])(\w+)(?:\s+([^\]]*))?\]/);
  if (!match) return null;
  return {
    full: match[0],
    comando: match[1] + match[2],
    query: (match[3] || '').trim(),
  };
}
